/**
 * PersonRow — one row in the AdminPeoplePage people table.
 *
 * Columns: PERSON | GOOGLE | NU-SYNC | THRIVE | HUBSPOT | NU-ASSIST | STATUS | ACTIONS
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RefreshCw } from "lucide-react";
import {
  checkPerson,
  offboardPerson,
  onboardPerson,
  setPersonOnLeave,
} from "./api";
import { ActionDialog } from "./ActionDialog";
import type { OperationResult, PersonSummary } from "./types";
import { STATUS_COLOR, STATUS_LABEL } from "./types";
import { Button, Spinner } from "@nu-assist/ui";

// ---------------------------------------------------------------------------
// Badge primitive
// ---------------------------------------------------------------------------

type BadgeVariant = "green" | "red" | "orange" | "yellow" | "blue" | "gray";

const BADGE_CLASS: Record<BadgeVariant, string> = {
  green: "bg-green-500/15 text-green-700 dark:text-green-400",
  red: "bg-red-500/15 text-red-700 dark:text-red-400",
  orange: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  yellow: "bg-yellow-400/15 text-yellow-700 dark:text-yellow-400",
  blue: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  gray: "bg-surface text-text-secondary",
};

function Badge({ label, variant, sub }: { label: string; variant: BadgeVariant; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-label font-medium ${BADGE_CLASS[variant]}`}
      >
        {label}
      </span>
      {sub && <span className="text-[11px] text-text-secondary">{sub}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-column derivations
// ---------------------------------------------------------------------------

function GoogleBadge({ suspended }: { suspended: boolean | null | undefined }) {
  if (suspended == null) return <Badge label="no data" variant="gray" />;
  if (suspended) return <Badge label="suspended" variant="red" />;
  return <Badge label="active" variant="green" />;
}

function NuSyncBadge({
  isActive,
  onLeaveUntil,
}: {
  isActive: boolean;
  onLeaveUntil: string | null | undefined;
}) {
  if (!isActive && onLeaveUntil) return <Badge label="on leave" variant="blue" sub={onLeaveUntil} />;
  if (!isActive) return <Badge label="inactive" variant="gray" />;
  return <Badge label="active" variant="green" />;
}

function ThriveBadge({ disabled }: { disabled: boolean | null | undefined }) {
  if (disabled == null) return <Badge label="no data" variant="gray" />;
  if (disabled) return <Badge label="disabled" variant="red" />;
  return <Badge label="active" variant="green" />;
}

function HubSpotBadge({ archived }: { archived: boolean | null | undefined }) {
  if (archived == null) return <Badge label="no data" variant="gray" />;
  if (archived) return <Badge label="user, no owner" variant="orange" />;
  return <Badge label="owner" variant="green" />;
}

function NuAssistCell({ hasLoggedIn }: { hasLoggedIn: boolean }) {
  return (
    <Badge label={hasLoggedIn ? "signed in" : "never"} variant={hasLoggedIn ? "green" : "gray"} />
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status;
  const color = STATUS_COLOR[status as keyof typeof STATUS_COLOR] ?? "text-text";

  let variant: BadgeVariant = "gray";
  if (color.includes("red")) variant = "red";
  else if (color.includes("orange")) variant = "orange";
  else if (color.includes("yellow")) variant = "yellow";
  else if (color.includes("blue")) variant = "blue";
  else if (color.includes("green")) variant = "green";

  return <Badge label={label} variant={variant} />;
}

// ---------------------------------------------------------------------------
// OffboardPanel — rich confirmation panel for offboard action
// ---------------------------------------------------------------------------

const STEP_COLOR: Record<string, string> = {
  ok: "text-green-600 dark:text-green-400",
  failed: "text-red-600 dark:text-red-400",
  skipped: "text-text-muted",
  manual: "text-yellow-600 dark:text-yellow-400",
};

export function OffboardPanel({
  person,
  onConfirm,
  onClose,
}: {
  person: PersonSummary;
  onConfirm: () => Promise<OperationResult>;
  onClose: () => void;
}) {
  const [state, setState] = useState<"confirm" | "running" | "result">("confirm");
  const [result, setResult] = useState<OperationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<Date | null>(null);

  async function handleOffboard() {
    setState("running");
    try {
      const r = await onConfirm();
      setResult(r);
      setCompletedAt(new Date());
      setState("result");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCompletedAt(new Date());
      setState("result");
    }
  }

  const statusLabel = STATUS_LABEL[person.status as keyof typeof STATUS_LABEL] ?? person.status;
  const statusColor = STATUS_COLOR[person.status as keyof typeof STATUS_COLOR] ?? "text-text";

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface rounded-md shadow-lg w-full max-w-2xl p-6 font-sans flex flex-col gap-4">
        {/* Header */}
        <div>
          <h2 className="text-h2 text-text font-semibold">{person.email} — Offboard</h2>
          {person.role && (
            <p className="text-label text-text-secondary capitalize mt-0.5">{person.role}</p>
          )}
        </div>

        {state === "confirm" && (
          <>
            {/* Person info */}
            <div className="rounded border border-border bg-surface-raised p-4 flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <span className="text-label text-text-secondary w-24 shrink-0">Status</span>
                <span className={`text-body font-medium ${statusColor}`}>{statusLabel}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-label text-text-secondary w-24 shrink-0">nu_sync</span>
                <span className="text-body">{person.is_active ? "Active" : person.on_leave_until ? `On leave until ${person.on_leave_until}` : "Inactive"}</span>
              </div>
              {person.thrive_disabled != null && (
                <div className="flex items-center gap-3">
                  <span className="text-label text-text-secondary w-24 shrink-0">Thrive</span>
                  <span className="text-body">{person.thrive_disabled ? "Disabled" : "Active"}</span>
                </div>
              )}
              {person.hubspot_owner_archived != null && (
                <div className="flex items-center gap-3">
                  <span className="text-label text-text-secondary w-24 shrink-0">HubSpot</span>
                  <span className="text-body">{person.hubspot_owner_archived ? "Owner archived" : "Owner active"}</span>
                </div>
              )}
            </div>
            <p className="text-body text-text-muted">
              Deactivates <span className="font-medium text-text">{person.email}</span> across nu_sync, Thrive, and HubSpot. Google Workspace account must be suspended manually.
            </p>
            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="danger" onClick={handleOffboard}>Offboard</Button>
            </div>
          </>
        )}

        {state === "running" && (
          <div className="flex items-center gap-3 py-4">
            <Spinner size="sm" />
            <span className="text-body text-text-muted">Offboarding…</span>
          </div>
        )}

        {state === "result" && (
          <>
            {error ? (
              <p className="text-body text-red-600">{error}</p>
            ) : result ? (
              <div className="space-y-2">
                <div className="flex items-baseline gap-3">
                  <p className={`text-body font-medium ${result.success ? "text-green-600" : "text-red-600"}`}>
                    {result.success ? "Offboard completed" : "Offboard completed with errors"}
                  </p>
                  {completedAt && (
                    <span className="text-label text-text-secondary">
                      {completedAt.toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-body">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="py-1 pr-4 text-label font-semibold text-text-secondary whitespace-nowrap">System</th>
                        <th className="py-1 pr-4 text-label font-semibold text-text-secondary whitespace-nowrap">Status</th>
                        <th className="py-1 text-label font-semibold text-text-secondary">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(result.steps).map(([system, step]) => {
                        const isManual = step.status === "manual";
                        return (
                          <tr
                            key={system}
                            className={`border-b border-border last:border-0 ${isManual ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}
                          >
                            <td className={`py-1.5 pr-4 capitalize whitespace-nowrap font-medium ${isManual ? "text-amber-800 dark:text-amber-300" : "text-text-muted"}`}>
                              {isManual && <span className="mr-1.5">⚠</span>}
                              {system.replace(/_/g, " ")}
                            </td>
                            <td className={`py-1.5 pr-4 whitespace-nowrap font-semibold ${STEP_COLOR[step.status] ?? ""}`}>
                              {step.status}
                            </td>
                            <td className={`py-1.5 text-sm ${isManual ? "text-amber-700 dark:text-amber-300 font-medium" : "text-text-muted"}`}>
                              {step.message ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            <div className="flex justify-end mt-2">
              <Button variant="secondary" onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface PersonRowProps {
  person: PersonSummary;
  onRefresh: () => void;
  onPersonClick: () => void;
}

type ActiveAction = "offboard" | "onboard" | "leave" | null;

export function PersonRow({ person, onRefresh, onPersonClick }: PersonRowProps) {
  const [activeAction, setActiveAction] = useState<ActiveAction>(null);
  const [onboardRole, setOnboardRole] = useState("partner");
  const [onboardFirstName, setOnboardFirstName] = useState("");
  const [onboardLastName, setOnboardLastName] = useState("");
  const [onboardLinkedin, setOnboardLinkedin] = useState("");
  const [onboardPhone, setOnboardPhone] = useState("");
  const [leaveReturnDate, setLeaveReturnDate] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [checking, setChecking] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuDropdownRef = useRef<HTMLDivElement>(null);
  const onboardSucceededRef = useRef(false);
  const [rehireContext, setRehireContext] = useState<{
    role: string;
    first_name: string;
    last_name: string;
    departed_name?: string;
    departed_at?: string;
  } | null>(null);
  const [releaseHolderContext, setReleaseHolderContext] = useState<{
    role: string;
    first_name: string;
    last_name: string;
    holder_name?: string;
    holder_disabled?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!showMenu) return;
    function handleOutsideClick(e: MouseEvent) {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current && !menuButtonRef.current.contains(e.target as Node) &&
        menuDropdownRef.current && !menuDropdownRef.current.contains(e.target as Node)
      ) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showMenu]);

  async function handleCheckNow(e: React.MouseEvent) {
    e.stopPropagation();
    setChecking(true);
    try {
      await checkPerson(person.email);
      onRefresh();
    } finally {
      setChecking(false);
    }
  }

  function buildOnConfirm(action: ActiveAction): () => Promise<OperationResult> {
    switch (action) {
      case "offboard":
        return () => offboardPerson(person.email);
      case "onboard":
        return async () => {
          const result = await onboardPerson(person.email, {
            role: onboardRole,
            first_name: onboardFirstName,
            last_name: onboardLastName,
            rehire: false,
            linkedin_handle: onboardLinkedin.trim() || null,
            phone: onboardPhone.trim() || null,
          });
          onboardSucceededRef.current = result.success;
          if (result.steps.nu_sync?.status === "address_held_by_departed") {
            const d = result.steps.nu_sync.detail as Record<string, string> | null | undefined;
            setRehireContext({ role: onboardRole, first_name: onboardFirstName, last_name: onboardLastName, departed_name: d?.departed_name, departed_at: d?.departed_at });
          }
          if (result.steps.nu_sync?.status === "address_held_by_other_person") {
            const d = result.steps.nu_sync.detail as Record<string, unknown> | null | undefined;
            setReleaseHolderContext({
              role: onboardRole,
              first_name: onboardFirstName,
              last_name: onboardLastName,
              holder_name: d?.holder_name as string | undefined,
              holder_disabled: d?.holder_disabled as boolean | undefined,
            });
          }
          return result;
        };
      case "leave":
        return () => setPersonOnLeave(person.email, { return_date: leaveReturnDate });
      default:
        return () => Promise.reject(new Error("Unknown action"));
    }
  }

  return (
    <>
      <tr data-testid={`person-row-${person.email}`} className="border-b border-border hover:bg-surface-hover transition-colors cursor-pointer">
        {/* PERSON — email as primary, role as subtitle */}
        <td className="py-2.5 px-3" onClick={onPersonClick}>
          <div className="flex flex-col gap-0.5">
            <span className="text-body font-medium text-accent hover:underline leading-tight">
              {person.email}
            </span>
            {person.role && (
              <span className="text-label text-text-secondary capitalize">{person.role}</span>
            )}
          </div>
        </td>

        {/* GOOGLE */}
        <td className="py-2.5 px-3">
          <GoogleBadge suspended={person.google_suspended} />
        </td>

        {/* THRIVE */}
        <td className="py-2.5 px-3">
          <ThriveBadge disabled={person.thrive_disabled} />
        </td>

        {/* HUBSPOT */}
        <td className="py-2.5 px-3">
          <HubSpotBadge archived={person.hubspot_owner_archived} />
        </td>

        {/* NU-ASSIST */}
        <td className="py-2.5 px-3">
          <NuAssistCell hasLoggedIn={person.has_logged_in} />
        </td>

        {/* NU-SYNC */}
        <td className="py-2.5 px-3">
          <NuSyncBadge isActive={person.is_active} onLeaveUntil={person.on_leave_until} />
        </td>

        {/* STATUS */}
        <td className="py-2.5 px-3">
          <StatusBadge status={person.status} />
        </td>

        {/* ACTIONS */}
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCheckNow}
              disabled={checking}
              title="Run live check"
              className="inline-flex items-center justify-center w-7 h-7 rounded text-text-secondary hover:text-text hover:bg-surface-raised transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${checking ? "animate-spin" : ""}`} aria-hidden="true" />
            </button>
            <div className="relative" ref={menuRef}>
              <button
                ref={menuButtonRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!showMenu) {
                    const rect = menuButtonRef.current?.getBoundingClientRect();
                    if (rect) {
                      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                    }
                  }
                  setShowMenu((v) => !v);
                }}
                className="inline-flex items-center justify-center w-7 h-7 rounded text-text-secondary hover:text-text hover:bg-surface-raised transition-colors text-base leading-none"
                title="More actions"
              >
                ⋯
              </button>
            </div>
          </div>
        </td>
      </tr>

      {activeAction === "offboard" && (
        <OffboardPanel
          person={person}
          onConfirm={buildOnConfirm("offboard")}
          onClose={() => { setActiveAction(null); onRefresh(); }}
        />
      )}

      {activeAction === "onboard" && (
        <ActionDialog
          title="Onboard staff member"
          description={`Create accounts for ${person.email} across nu_sync, Thrive, and HubSpot. Engineers are added to nu_sync only (no Thrive seat). Onboarded on ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`}
          confirmLabel="Onboard"
          confirmDisabled={!onboardFirstName.trim() || !onboardLastName.trim()}
          onConfirm={buildOnConfirm("onboard")}
          onClose={async () => {
            setActiveAction(null);
            setOnboardRole("partner");
            setOnboardFirstName("");
            setOnboardLastName("");
            setOnboardLinkedin("");
            setOnboardPhone("");
            if (onboardSucceededRef.current) {
              onboardSucceededRef.current = false;
              try { await checkPerson(person.email); } catch { /* best-effort */ }
            }
            onRefresh();
          }}
        >
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-label text-text-secondary mb-1">First name</label>
                <input
                  className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text"
                  value={onboardFirstName}
                  onChange={(e) => setOnboardFirstName(e.target.value)}
                  placeholder="Jane"
                  autoFocus
                />
              </div>
              <div className="flex-1">
                <label className="block text-label text-text-secondary mb-1">Last name</label>
                <input
                  className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text"
                  value={onboardLastName}
                  onChange={(e) => setOnboardLastName(e.target.value)}
                  placeholder="Smith"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-label text-text-secondary mb-1">Role</label>
                <select
                  className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text"
                  value={onboardRole}
                  onChange={(e) => setOnboardRole(e.target.value)}
                >
                  <option value="partner">Partner</option>
                  <option value="recruiter">Recruiter</option>
                  <option value="engineer">Engineer</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-label text-text-secondary mb-1">LinkedIn handle <span className="text-text-muted">(optional)</span></label>
                <input
                  className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text placeholder:text-text-muted"
                  value={onboardLinkedin}
                  onChange={(e) => setOnboardLinkedin(e.target.value)}
                  placeholder="jsmith"
                />
              </div>
              <div className="flex-1">
                <label className="block text-label text-text-secondary mb-1">Phone <span className="text-text-muted">(optional)</span></label>
                <input
                  type="tel"
                  className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text placeholder:text-text-muted"
                  value={onboardPhone}
                  onChange={(e) => setOnboardPhone(e.target.value)}
                  placeholder="+1 555 000 0000"
                />
              </div>
            </div>
          </div>
        </ActionDialog>
      )}

      {rehireContext !== null && activeAction === null && (
        <ActionDialog
          title="Rehire staff member?"
          description={`The address ${person.email} is held by ${rehireContext.departed_name ?? "a departed person"}${rehireContext.departed_at ? ` (departed ${rehireContext.departed_at})` : ""}. Is this the same person returning to the same role?`}
          confirmLabel="Yes, rehire"
          onConfirm={() => onboardPerson(person.email, { role: rehireContext.role, first_name: rehireContext.first_name, last_name: rehireContext.last_name, rehire: true })}
          onClose={() => { setRehireContext(null); onRefresh(); }}
        />
      )}

      {releaseHolderContext !== null && activeAction === null && (
        <ActionDialog
          title="Address held by another person"
          description={`The address ${person.email} is currently assigned to ${releaseHolderContext.holder_name ?? "another person"} in Thrive${releaseHolderContext.holder_disabled ? " (disabled)" : ""}. Releasing it will rename their Thrive email to first.last@nusearch.com. Do you want to proceed?`}
          confirmLabel="Release and onboard"
          onConfirm={() => onboardPerson(person.email, {
            role: releaseHolderContext.role,
            first_name: releaseHolderContext.first_name,
            last_name: releaseHolderContext.last_name,
            rehire: false,
            release_previous_holder: true,
          })}
          onClose={() => { setReleaseHolderContext(null); onRefresh(); }}
        />
      )}

      {activeAction === "leave" && (
        <ActionDialog
          title="Set on leave"
          description={`Set ${person.email} on leave.`}
          confirmLabel="Set on leave"
          confirmDisabled={!leaveReturnDate}
          onConfirm={buildOnConfirm("leave")}
          onClose={() => { setActiveAction(null); onRefresh(); }}
        >
          <label className="block text-label text-text-secondary mb-1">Return date</label>
          <input
            type="date"
            className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text"
            value={leaveReturnDate}
            onChange={(e) => setLeaveReturnDate(e.target.value)}
          />
        </ActionDialog>
      )}

      {showMenu && menuPos && createPortal(
        <div
          ref={menuDropdownRef}
          style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
          className="bg-surface border border-border rounded-md shadow-lg min-w-[148px]"
        >
          {person.is_active ? (
            <>
              <MenuButton label="Set on leave" onClick={() => { setActiveAction("leave"); setShowMenu(false); }} />
              <MenuButton label="Offboard" onClick={() => { setActiveAction("offboard"); setShowMenu(false); }} />
            </>
          ) : person.status === "needs_onboarding" ? (
            <MenuButton label="Onboard" onClick={() => { setActiveAction("onboard"); setShowMenu(false); }} />
          ) : null}
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-2 text-body text-text hover:bg-surface-hover transition-colors"
    >
      {label}
    </button>
  );
}
